import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  AuditEventTypeSchema,
  AuditRecordSchema,
  AuditRecordTypeSchema,
  UserSummarySchema,
} from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { getData, getPage } from '../../shared/api/client';
import { AUDIT_RECORDS, AuditTable, eventLabel } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { SearchFilterBar } from '../../shared/ui/filters';

const PAGE_SIZE = 50;
const FILTERS = [
  'subject_record_type',
  'subject_record_id',
  'actor_user_id',
  'event_type',
  'from',
  'to',
  'correlation',
] as const;

/**
 * The audit trail (FRD-CORE-030 to CORE-033, USR-014): every create, change, status change,
 * approval, posting, access, unmasked access, export, configuration change, SoD block and refused
 * sign-in, searchable by record, actor, event, date and correlation ID (SDD §28.4). Read-only —
 * nothing here changes the trail. The filters live in the address, so a search survives a refresh
 * and can be shared.
 */
export function AuditTrailPage() {
  const session = useSession();
  const canSeeUsers = hasCapability(session.data, 'platform.app_user:view');
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1') || 1;
  const filters = Object.fromEntries(
    FILTERS.map((key) => [key, params.get(key) ?? '']).filter(([, v]) => v !== ''),
  ) as Partial<Record<(typeof FILTERS)[number], string>>;
  const [draft, setDraft] = useState(filters);
  // The form follows the address: a correlation link (or back/forward) changes the filters in the
  // URL, and the fields must show them, or the next Search would drop them. State is adjusted
  // while rendering when the address changes (React's pattern for deriving state from props).
  const filterKey = FILTERS.map((key) => params.get(key) ?? '').join('\u0000');
  const [shownKey, setShownKey] = useState(filterKey);
  if (shownKey !== filterKey) {
    setShownKey(filterKey);
    setDraft(filters);
  }

  const types = useQuery({
    queryKey: ['platform', 'audit-record-types'],
    queryFn: () => getData('/api/v1/platform/audit-record-types', z.array(AuditRecordTypeSchema)),
  });
  const users = useQuery({
    queryKey: ['platform', 'users', 'all'],
    enabled: canSeeUsers,
    queryFn: () =>
      getPage('/api/v1/platform/users?pageSize=200&sort=displayName', UserSummarySchema),
  });
  const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  for (const [key, value] of Object.entries(filters)) query.set(`filter[${key}]`, value);
  const records = useQuery({
    queryKey: ['platform', 'audit', query.toString()],
    queryFn: () => getPage(`${AUDIT_RECORDS}?${query.toString()}`, AuditRecordSchema),
  });

  const apply = (next: typeof filters, nextPage = 1) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(next)) if (value !== '') search.set(key, value);
    if (nextPage > 1) search.set('page', String(nextPage));
    setParams(search);
  };
  const set = (key: (typeof FILTERS)[number], value: string) => {
    setDraft({ ...draft, [key]: value });
  };
  const total = records.data?.total ?? 0;

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Audit trail"
        description="Who did what, to which record, and when. The trail cannot be changed by anyone."
      />
      <SearchFilterBar
        aria-label="Search the audit trail"
        onSubmit={() => {
          apply(draft);
        }}
        actions={
          <>
            <Button type="submit" variant="contained">
              Search
            </Button>
            <Button
              onClick={() => {
                setDraft({});
                apply({});
              }}
            >
              Clear
            </Button>
          </>
        }
      >
        <TextField
          select
          label="Record type"
          size="small"
          sx={{ minWidth: 200 }}
          value={draft.subject_record_type ?? ''}
          onChange={(e) => {
            setDraft({ ...draft, subject_record_type: e.target.value, subject_record_id: '' });
          }}
        >
          <MenuItem value="">Any</MenuItem>
          {(types.data ?? []).map((t) => (
            <MenuItem key={t.code} value={t.code}>
              {t.displayName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Record number"
          size="small"
          disabled={(draft.subject_record_type ?? '') === ''}
          value={draft.subject_record_id ?? ''}
          onChange={(e) => {
            set('subject_record_id', e.target.value.replace(/\D/g, ''));
          }}
          helperText="The record's system number"
        />
        <TextField
          select
          label="Event"
          size="small"
          sx={{ minWidth: 180 }}
          value={draft.event_type ?? ''}
          onChange={(e) => {
            set('event_type', e.target.value);
          }}
        >
          <MenuItem value="">Any</MenuItem>
          {AuditEventTypeSchema.options.map((t) => (
            <MenuItem key={t} value={t}>
              {eventLabel(t)}
            </MenuItem>
          ))}
        </TextField>
        {canSeeUsers && (
          <TextField
            select
            label="By user"
            size="small"
            sx={{ minWidth: 200 }}
            value={draft.actor_user_id ?? ''}
            onChange={(e) => {
              set('actor_user_id', e.target.value);
            }}
          >
            <MenuItem value="">Anyone</MenuItem>
            {(users.data?.items ?? []).map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.displayName}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="From"
          type="date"
          size="small"
          value={draft.from ?? ''}
          onChange={(e) => {
            set('from', e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={draft.to ?? ''}
          onChange={(e) => {
            set('to', e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Correlation ID"
          size="small"
          sx={{ minWidth: 200 }}
          value={draft.correlation ?? ''}
          onChange={(e) => {
            set('correlation', e.target.value.trim());
          }}
          helperText="Every record written by one request"
        />
      </SearchFilterBar>
      <Section
        title={
          records.data === undefined
            ? 'Results'
            : `${String(total)} ${total === 1 ? 'record' : 'records'}`
        }
        actions={
          total > PAGE_SIZE ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="caption">
                Page {page} of {Math.ceil(total / PAGE_SIZE)}
              </Typography>
              <Button
                size="small"
                disabled={page === 1}
                onClick={() => {
                  apply(filters, page - 1);
                }}
              >
                Newer
              </Button>
              <Button
                size="small"
                disabled={page * PAGE_SIZE >= total}
                onClick={() => {
                  apply(filters, page + 1);
                }}
              >
                Older
              </Button>
            </Stack>
          ) : undefined
        }
      >
        {records.isPending && <LoadingState label="Searching the audit trail" />}
        {records.isError && <ErrorState message={records.error.message} />}
        {records.data?.items.length === 0 && <EmptyState title="Nothing matches" />}
        {records.data !== undefined && records.data.items.length > 0 && (
          <AuditTable records={records.data.items} showSubject label="Audit records" />
        )}
      </Section>
    </Stack>
  );
}
